import { useEffect, useId, useMemo, useState } from 'react';
import { cityLabel, loadCities, rankCities, type CityHit } from '../data/cities';
import type { Lang, Location } from '../domain/types';
import { useI18n } from '../i18n';
import type { CityPick } from '../repositories/types';
import { Icon } from './Icon';

/** The picked city, or an existing Location row (when editing someone who already has one). */
export type CityValue = { kind: 'city'; city: CityPick } | { kind: 'location'; location: Location } | null;

export function cityValueLabel(v: CityValue, lang: Lang): string {
  if (!v) return '';
  if (v.kind === 'location') return lang === 'he' ? v.location.labelHe || v.location.label : v.location.label;
  return cityLabel(v.city, lang);
}

export function toCityPick(c: CityHit): CityPick {
  return { ref: c.ref, name: c.name, nameHe: c.nameHe, countryCode: c.countryCode, country: c.country, countryHe: c.countryHe, lat: c.lat, lng: c.lng, timezone: c.timezone };
}

/**
 * Search-and-select a city from the offline list (loaded on first keystroke).
 * Shows "City, Country" in the reader's language; the chosen city sits in a
 * soft card with a Change button, like the person picker.
 */
export function CityPicker({
  value, onChange, label, autoFocus, allowClear = true, area, onAreaChange,
}: {
  value: CityValue;
  onChange: (v: CityValue) => void;
  label?: string;
  autoFocus?: boolean;
  allowClear?: boolean;
  /** When given, an optional "be more precise" text field is shown under the chosen city. */
  area?: string;
  onAreaChange?: (area: string) => void;
}) {
  const { t, lang } = useI18n();
  const id = useId();
  const [query, setQuery] = useState('');
  const [cities, setCities] = useState<CityHit[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [showArea, setShowArea] = useState(!!area);

  // Load the city list the first time someone types.
  useEffect(() => {
    if (!query.trim() || cities || loading) return;
    setLoading(true);
    void loadCities().then((list) => {
      setCities(list);
      setLoading(false);
    });
  }, [query, cities, loading]);

  const results = useMemo(() => (cities ? rankCities(cities, query, lang, 8) : []), [cities, query, lang]);

  if (value) {
    return (
      <div className="field">
        {label ? <span className="field-label">{label}</span> : null}
        <div className="row-nowrap card-soft" style={{ padding: 'var(--s-2) var(--s-3)' }}>
          <Icon name="pin" />
          <span className="grow bold">{cityValueLabel(value, lang)}</span>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => {
              onChange(null);
              setQuery('');
            }}
          >
            {allowClear ? t('places.change') : t('common.search')}
          </button>
        </div>
        {onAreaChange && (
          showArea ? (
            <div className="field mt-2">
              <label className="field-label" htmlFor={`${id}-area`}>{t('places.preciseLabel')}</label>
              <input id={`${id}-area`} className="input" value={area ?? ''} onChange={(e) => onAreaChange(e.target.value)} autoComplete="off" />
              <span className="help">{t('places.preciseHelp')}</span>
            </div>
          ) : (
            <button type="button" className="btn btn-ghost btn-sm mt-1" onClick={() => setShowArea(true)}>
              <Icon name="plus" />
              <span>{t('places.precise')}</span>
            </button>
          )
        )}
      </div>
    );
  }

  return (
    <div className="field">
      {label ? (
        <label className="field-label" htmlFor={id}>
          {label}
        </label>
      ) : null}
      <input
        id={id}
        className="input"
        value={query}
        autoFocus={autoFocus}
        placeholder={t('places.searchHint')}
        onChange={(e) => setQuery(e.target.value)}
        role="combobox"
        aria-expanded={results.length > 0}
        aria-controls={`${id}-list`}
        autoComplete="off"
        inputMode="search"
      />
      <ul className="list-reset" id={`${id}-list`} style={{ maxHeight: 280, overflowY: 'auto' }}>
        {results.map((c) => (
          <li key={c.ref}>
            <button
              type="button"
              className="person-row"
              onClick={() => {
                onChange({ kind: 'city', city: toCityPick(c) });
                setQuery('');
              }}
            >
              <span className="city-dot" aria-hidden="true"><Icon name="pin" /></span>
              <span className="grow">
                <span className="name">{lang === 'he' ? c.nameHe || c.name : c.name}</span>
                <span className="sub"> {lang === 'he' ? c.countryHe : c.country}</span>
              </span>
              <Icon name="forward" className="icon-mirror faint" />
            </button>
          </li>
        ))}
        {loading && query.trim() ? <li className="muted small" style={{ padding: 'var(--s-2)' }}>{t('places.searching')}</li> : null}
        {!loading && cities && query.trim() && results.length === 0 ? <li className="muted small" style={{ padding: 'var(--s-2)' }}>{t('places.noResults')}</li> : null}
      </ul>
    </div>
  );
}
